/**
 * 🌍 GLOBAL STANDART KOD (TÜM PROJELER İÇİN GEÇERLİDİR)
 *
 * Bu dosya QubePro ekosisteminde ortak (global) standarttır.
 * Tek kaynak `qubepro-core` reposudur; uydu repolarda kalıcı değişiklik yapılmaz.
 * Değişiklik gerekiyorsa Core'da güncellenir ve senkron ile diğer repolara yayılır.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useModalLabels } from './ModalLabels';

import './Alert.css';

export interface AlertProps {
  /** Uyarı tipi: başarı veya hata */
  type: 'success' | 'error';
  /** Metin mesajı (children kullanılmıyorsa); API/hook çıktısı null olabilir */
  message?: string | null;
  /** Ek içerik — mesajın altında gösterilir */
  children?: React.ReactNode;
  /**
   * Kapatıldığında çağrılır (tercih: parent state temizliği).
   * Verilmezse bile ✕ gösterilir; sadece bileşen içinde geçici olarak gizlenir (mesaj değişince yeniden görünür).
   */
  onDismiss?: () => void;
  /** inline: akış içi; banner: sayfa içeriğinin en üstünde yapışkan şerit */
  placement?: 'inline' | 'banner';
  /** false ise kapatma düğmesi gösterilmez */
  dismissible?: boolean;
  /** Ek inline stil */
  style?: React.CSSProperties;
}

/**
 * Paylaşımlı uyarı bileşeni.
 * `success-message` / `error-message` CSS sınıflarını kullanır (index.css).
 */
export function Alert({
  type,
  message,
  children,
  onDismiss,
  placement = 'inline',
  dismissible = true,
  style,
}: AlertProps) {
  const labels = useModalLabels();
  const content = children ?? message;
  const [dismissedSignature, setDismissedSignature] = useState<string | null>(null);

  useEffect(() => {
    if (!content) setDismissedSignature(null);
  }, [content]);

  const signature =
    typeof message === 'string' && message
      ? `${type}:${message}`
      : `${type}:${children ? 'children' : ''}`;

  const handleDismiss = useCallback(() => {
    onDismiss?.();
    setDismissedSignature(signature);
  }, [onDismiss, signature]);

  if (!content) return null;
  if (dismissedSignature === signature) return null;

  const baseClass = type === 'success' ? 'success-message' : 'error-message';
  const className = [
    baseClass,
    'alert',
    placement === 'banner' ? 'alert--banner' : '',
    dismissible ? 'alert--with-dismiss' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      className={className}
      style={{
        position: placement === 'inline' ? 'relative' : undefined,
        ...style,
      }}
      role="alert"
    >
      {dismissible && (
        <button
          type="button"
          className="alert__close"
          onClick={handleDismiss}
          title={labels.close}
          aria-label={labels.close}
        >
          ×
        </button>
      )}
      {content}
    </div>
  );
}
